import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '../../Modal'
import { Icon } from '../../../atoms/Icon'
import { ScoreRadar } from '../ScoreRadar'
import { ScoreBreakdownItem } from './ScoreBreakdownItem'
import { ScoreRadarModalProps, ScoreDefinition } from './ScoreRadarModal.types'
import {
  ModalBody,
  ModalTitle,
  TopSection,
  RadarSection,
  OverallScoreSection,
  OverallScoreTitle,
  OverallScoreValue,
  OverallScoreDescription,
  InfoNote,
  SectionDivider,
  BreakdownSection,
  BreakdownTitle,
  BreakdownList,
  MethodologySection,
  MethodologyTitle,
  MethodologySubsection,
  MethodologySubtitle,
  MethodologyList,
} from './ScoreRadarModal.styles'

const getScoreColorInternal = (score: number): string => {
  if (score >= 70) return '#10B981'
  if (score >= 40) return '#F59E0B'
  return '#3B82F6'
}

export const ScoreRadarModal: React.FC<ScoreRadarModalProps> = ({
  isOpen,
  onClose,
  property,
  getScoreColor,
}) => {
  const [animate, setAnimate] = useState(false)

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setAnimate(true), 100)
      return () => clearTimeout(timer)
    } else {
      setAnimate(false)
    }
  }, [isOpen])

  const { t } = useTranslation()
  const developmentScore = property.development_score ?? 0
  const colorFn = getScoreColor ?? getScoreColorInternal
  const scoreColor = colorFn(developmentScore)

  const scores: ScoreDefinition[] = [
    {
      key: 'potential',
      radarLabel: t('common.scoreModal.scores.potential.radarLabel'),
      displayLabel: t('common.scoreModal.scores.potential.displayLabel'),
      description: t('common.scoreModal.scores.potential.description'),
      value: property.under_exploited_score ?? 0,
    },
    {
      key: 'legacy',
      radarLabel: t('common.scoreModal.scores.legacy.radarLabel'),
      displayLabel: t('common.scoreModal.scores.legacy.displayLabel'),
      description: t('common.scoreModal.scores.legacy.description'),
      value: property.existing_score ?? 0,
    },
    {
      key: 'geometry',
      radarLabel: t('common.scoreModal.scores.geometry.radarLabel'),
      displayLabel: t('common.scoreModal.scores.geometry.displayLabel'),
      description: t('common.scoreModal.scores.geometry.description'),
      value: property.geo_shape_score ?? 0,
    },
    {
      key: 'livability',
      radarLabel: t('common.scoreModal.scores.livability.radarLabel'),
      displayLabel: t('common.scoreModal.scores.livability.displayLabel'),
      description: t('common.scoreModal.scores.livability.description'),
      value: property.livability_score ?? 0,
    },
  ]

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="700px">
      <ModalBody>
        <ModalTitle>{t('common.scoreModal.title')}</ModalTitle>

        <TopSection>
          <RadarSection>
            <ScoreRadar
              property={property}
              size={350}
              getScoreColor={colorFn}
            />
          </RadarSection>

          <OverallScoreSection>
            <OverallScoreTitle>
              {t('common.scoreModal.overallScore')}
            </OverallScoreTitle>
            <OverallScoreValue $color={scoreColor}>
              {Math.round(developmentScore)}/100
            </OverallScoreValue>
            <OverallScoreDescription>
              {t('common.scoreModal.overallScoreDescription')}
            </OverallScoreDescription>
            <InfoNote>
              <Icon name="info" size="small" />
              <span>{t('common.scoreModal.scoresUpdated')}</span>
            </InfoNote>
          </OverallScoreSection>
        </TopSection>

        <SectionDivider />

        <BreakdownSection>
          <BreakdownTitle>
            {t('common.scoreModal.scoreBreakdown')}
          </BreakdownTitle>
          <BreakdownList>
            {scores.map((score, index) => (
              <ScoreBreakdownItem
                key={score.key}
                radarLabel={score.radarLabel}
                displayLabel={score.displayLabel}
                description={score.description}
                value={score.value}
                animationDelay={index * 150}
                animate={animate}
              />
            ))}
          </BreakdownList>
        </BreakdownSection>

        <MethodologySection>
          <MethodologyTitle>
            {t('common.scoreModal.methodology.title')}
          </MethodologyTitle>

          <MethodologySubsection>
            <MethodologySubtitle>
              {t('common.scoreModal.methodology.howCalculated')}
            </MethodologySubtitle>
            <MethodologyList>
              <li>{t('common.scoreModal.methodology.range')}</li>
              <li>{t('common.scoreModal.methodology.weightedCombination')}</li>
              <li>{t('common.scoreModal.methodology.dataSources')}</li>
            </MethodologyList>
          </MethodologySubsection>

          <MethodologySubsection>
            <MethodologySubtitle>
              {t('common.scoreModal.methodology.limitations')}
            </MethodologySubtitle>
            <MethodologyList>
              <li>{t('common.scoreModal.methodology.generalPotential')}</li>
              <li>{t('common.scoreModal.methodology.localRegulations')}</li>
              <li>{t('common.scoreModal.methodology.marketConditions')}</li>
              <li>{t('common.scoreModal.methodology.dataAge')}</li>
            </MethodologyList>
          </MethodologySubsection>
        </MethodologySection>
      </ModalBody>
    </Modal>
  )
}
